import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { DemoPill, Segmented, StateMessage } from '@/components/ui/controls';
import { PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { Message, MessageStatus } from './api-types';
import MessagingNav from './messaging-nav';
import { ChannelList, MessageStatusBadge, SegmentList, deliverySummary, messageDate } from './parts';

type Filter = 'all' | 'sent' | 'scheduled' | 'draft' | 'failed';

// Sending shows under Scheduled: it is on its way.
const inFilter = (m: Message, f: Filter) => f === 'all' || m.status === f || (f === 'scheduled' && m.status === 'sending');

const COLUMNS: SortColumns<Message> = {
  title: { value: (m) => m.title },
  audience: { value: (m) => (m.status === 'sent' ? m.audience : undefined), desc: true },
  // The date the Date column shows: sent, scheduled for, or last edited.
  date: {
    value: (m) => dateValue(m.status === 'sent' ? m.sent_at : m.status === 'scheduled' || m.status === 'sending' ? m.scheduled_at : m.updated_at),
    desc: true,
  },
};

/** Every message: sent, scheduled, still a draft, or failed (OUT-1…4, SEG-3). Automated ones go to one lead each. */
const MessagesPage = () => {
  const navigate = useNavigate();
  const { data, error } = useApi<Message[]>('/messages');
  const [filter, setFilter] = useState<Filter>('all');
  const all = data ?? [];
  const { rows, sort, toggle } = useSort(
    useMemo(() => all.filter((m) => inFilter(m, filter)), [all, filter]),
    COLUMNS,
  );
  const count = (f: Filter) => all.filter((m) => inFilter(m, f)).length;
  const open = (id: string) => navigate(`/messaging/messages/${id}`);
  const options: { value: Filter; label: string }[] = [
    { value: 'all', label: `All (${all.length})` },
    { value: 'sent', label: `Sent (${count('sent')})` },
    { value: 'scheduled', label: `Scheduled (${count('scheduled')})` },
    { value: 'draft', label: `Drafts (${count('draft')})` },
  ];
  if (count('failed')) options.push({ value: 'failed', label: `Failed (${count('failed')})` });

  return (
    <div>
      <PageHeader
        title="Messages"
        description="Updates that went out, are scheduled, or are still being written."
        actions={
          <Link to="/messaging/messages/new" className={btn.primary}>
            New message
          </Link>
        }
      >
        <MessagingNav />
        <div className="mb-4">
          <Segmented<Filter> label="Status" value={filter} onChange={setFilter} options={options} />
        </div>
      </PageHeader>

      {error && !data ? (
        <StateMessage title="Messages could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : data && rows.length === 0 ? (
        <StateMessage title={filter === 'all' ? 'No messages yet' : 'Nothing here'}>
          <Link to="/messaging/messages/new" className={btn.link}>
            Write a message
          </Link>
        </StateMessage>
      ) : (
        <div className={table.wrap}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'md:min-w-[960px]')}>
              <thead className={classNames(table.head, 'hidden md:table-header-group')}>
                <tr>
                  <SortTh label="Title" column="title" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>Channels</th>
                  <th scope="col" className={table.th}>Segments</th>
                  <SortTh label="Audience" column="audience" sort={sort} onSort={toggle} right />
                  <th scope="col" className={table.th}>Status</th>
                  <SortTh label="Date" column="date" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {!data &&
                  Array.from({ length: 4 }).map((_, i) => (
                    <tr key={i} className={table.row}>
                      <td colSpan={7} className={table.td}>
                        <div className="h-4 w-full animate-pulse rounded bg-layer" />
                      </td>
                    </tr>
                  ))}
                {rows.map((m) => (
                  <tr
                    key={m.id}
                    tabIndex={0}
                    onClick={() => open(m.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open(m.id);
                      }
                    }}
                    className={classNames(table.rowLink, 'align-top focus:outline-none focus-visible:bg-surface-hover')}
                  >
                    <td className={table.td}>
                      <p className="flex flex-wrap items-center gap-2 break-words text-fg-strong">
                        {m.title}
                        {m.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}
                      </p>
                      {m.trigger_id && <p className="text-xs text-fg-muted">Automated: {m.trigger_name || 'a deleted trigger'}</p>}
                      {/* On a phone the other columns fold in here. */}
                      <div className="mt-2 space-y-1.5 md:hidden">
                        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
                          <MessageStatusBadge status={m.status as MessageStatus} />
                          {messageDate(m)}
                          <ChannelList channels={m.channels} iconsOnly />
                        </p>
                        <SegmentList ids={m.segment_ids} empty={m.lead_id ? 'One lead' : '—'} />
                        {m.status === 'sent' && (
                          <p className="text-xs text-fg-muted">
                            {m.audience === 1 ? '1 person' : `${m.audience} people`} · {deliverySummary(m)}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <ChannelList channels={m.channels} iconsOnly />
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <SegmentList ids={m.segment_ids} empty={m.lead_id ? 'One lead' : '—'} />
                    </td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums md:table-cell')}>{m.status === 'sent' ? m.audience : '—'}</td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <MessageStatusBadge status={m.status as MessageStatus} />
                    </td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap text-fg-muted md:table-cell')}>{messageDate(m)}</td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap text-fg-muted md:table-cell')}>{deliverySummary(m)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-line px-4 py-3 text-sm text-fg-muted">
            {rows.length} {rows.length === 1 ? 'message' : 'messages'}
          </p>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;
